import { AcIcon, AcTooltip, AcTypography } from '@components'
import { ICONS } from '@constants'
import { handleKeyDown } from '@helpers/handle-key-down.helper'
import clsx from 'clsx'
import { ButtonHTMLAttributes, forwardRef, useMemo } from 'react'
import styles from './ac-button.module.scss'

type IHandleClick<T> = (
  event: React.MouseEvent<T> | React.KeyboardEvent<T>
) => unknown

interface IAcButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  onClick?: IHandleClick<HTMLButtonElement>
  label: string
  icon?: keyof typeof ICONS
  iconPosition?: 'before' | 'after'
  color?: 'primary' | 'secondary' | 'light' | 'dark'
  variant?: 'default' | 'text' | 'icon' | 'push' | 'icon-filled'
  mode?: 'light' | 'dark'
  /** @default 'center' */
  justifyContent?: 'center' | 'flex-start'
  /** @default 'default' */
  padding?: 'xs' | 'default' | 'md' | 'chip' | 'none'
  fullWidth?: boolean
  withToolTip?: boolean
  className?: string
  alignment?: 'left' | 'right'
  invertIcon?: boolean
}

export const AcButton = forwardRef<HTMLButtonElement, IAcButtonProps>(
  (
    {
      onClick,
      label,
      icon,
      color = 'primary',
      variant = 'default',
      disabled = false,
      withToolTip = false,
      mode = 'light',
      fullWidth,
      className,
      padding = 'default',
      justifyContent = 'center',
      iconPosition = 'after',
      alignment = 'left',
      invertIcon = false,
      ...props
    }: IAcButtonProps,
    ref
  ) => {
    const handleClick: IHandleClick<HTMLButtonElement> = event => {
      if (onClick && !disabled) onClick(event)
    }

    const getButtonClassNames = useMemo(
      () =>
        clsx(
          styles['ac-button'],
          styles[`ac-button-variant-${variant}`],
          styles[`ac-button-variant-${variant}-color-${color}`],
          styles[`ac-button-variant-${variant}-color-${color}-${mode}`],
          padding && styles[`ac-button--padding-${padding}`],
          fullWidth && styles['ac-button--full-width'],
          icon && styles[`ac-button-variant-${variant}--has-icon`],
          icon &&
            invertIcon &&
            styles[`ac-button-variant-${variant}--icon-inverted`],
          disabled && styles[`ac-button-variant-${variant}--disabled`],
          styles[`ac-button--alignment-${alignment}`],
          className && className
        ),
      [label, icon, variant, className, disabled, color, padding, fullWidth]
    )
    const getIcon = useMemo(() => {
      const iconClasses = clsx(
        styles['ac-button-icon-wrapper'],
        styles[`ac-button-icon-wrapper--${iconPosition}`]
      )
      return (
        <span className={iconClasses}>
          <AcIcon
            size={variant === 'icon-filled' ? 'md' : 'sm'}
            icon={icon}
            className={styles['ac-button-icon']}
          />
        </span>
      )
    }, [variant, icon])

    return (
      <AcTooltip
        visible={withToolTip}
        label={label}>
        <button
          ref={ref}
          onClick={handleClick}
          onKeyDown={e => handleKeyDown(e, () => handleClick)}
          disabled={disabled}
          className={getButtonClassNames}
          type="button"
          aria-label={label}
          {...props}>
          <span
            className={styles['ac-button-inner']}
            style={{ justifyContent }}>
            {icon && iconPosition === 'before' && getIcon}
            {label && variant !== 'icon' && variant !== 'icon-filled' && (
              <AcTypography
                element="span"
                size="sm"
                weight="semibold"
                className={styles['ac-button-label']}>
                {label}
              </AcTypography>
            )}
            {icon && iconPosition === 'after' && getIcon}
          </span>
        </button>
      </AcTooltip>
    )
  }
)
AcButton.displayName = 'AcButton'
